/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import {
  Avatar,
  Button,
  DialogActions,
  DialogContentText,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import ButtonCircle from '../ButtonCircle';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { IObject, TObjectFilter } from 'src/models/objectManagement';
import { reducerUpdateAnalyticArgumentObjectIdList } from 'src/features/analyticArguments';
import { useLocation } from 'react-router';
import SectionSocialProfile from './components/SectionSocialProfile';
import SectionKeywordHashtag from './components/SectionKeywordHashtag';
import SectionHashtag from './components/SectionHashtag';
import CloseIcon from '@mui/icons-material/Close';
import TooltipCustomize from '../TooltipCustomize';
import useCustomDashboard from 'src/services/useCustomDashboard';
import { reducerUpdateDashboardList } from 'src/features/customDashboard';
import {
  reducerUpdateGroupObject,
  reducerUpdateObjectListByObjectType,
  reducerUpdateSelectedGroups,
  reducerUpdateSelectedObjectType
} from 'src/features/objectManagement';
import { SETTINGS_GROUP } from 'src/route';
import useObjectManagement from 'src/services/useObjectManagement';
import { useAlert } from 'src/hooks/useAlert';
import { objectTypeOptions } from 'src/content/settings/MonitoredAssets/components/HeaderObjects';
import FilterObjectByPlatform, {
  SOCIAL_MEDIA_LIST
} from '../FilterObjectByPlatform';
import { TSocialMedia } from 'src/models/general';
import ProductTourTooltip from '../ProductTourTooltip';
import { useInactiveObject } from 'src/hooks/useInactiveObject';
import { ADD_OBJECT_TO_GROUP_VALIDATION } from 'src/constants';
import { TEcommerce } from '../EcommerceObjectPreview';
import FilterObjectByPlatformEcommerce, {
  ECOMMERCE_LIST
} from '../FilterObjectByPlatformEcommerce';
import useEcommerce from 'src/services/useEcommerce';

interface Props {
  title?: string;
  isCampaign?: boolean;
  isHidden?: boolean;
  onCloseEdit?: () => void;
  isEditing?: boolean;
  isDashboard?: boolean;
  showFilter?: boolean;
  onClickNextTour?: () => void;
}

const ModalManageObjectByGroup: React.FC<Props> = ({
  title,
  isCampaign = false,
  isHidden = false,
  isEditing = false,
  onCloseEdit,
  isDashboard = false,
  showFilter = false,
  onClickNextTour
}) => {
  const theme = useTheme();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { pathname, search } = useLocation();
  const pathSocialMedia = pathname.split('/')[2];
  const mainPath = pathname.split('/')[1];
  const isGoogleReview = mainPath === 'google_review';
  const isStreamPanel = mainPath === 'stream_panel';
  const params = new URLSearchParams(search);
  const tab = params.get('tab');
  const isEcommerce = pathname.includes('ecommerce') || tab === 'ecommerce';
  const [open, setOpen] = useState(false);
  const [hide, setHide] = useState(false);
  const { objectManagementEditDashboard } = useCustomDashboard();
  const { objectManagementEditGroup } = useObjectManagement();
  const { selectedObjectType, ecommerceObjectList } = useAppSelector(
    (state) => state.objectManagement
  );
  const { handleClickAlert } = useAlert();
  const autoRefreshInterval = useAppSelector(
    (state) => state.autoRefreshMetric.selectedAutoRefreshInterval
  );
  const { isInactiveObjectExist } = useInactiveObject();

  const [selectedPlatforms, setSelectedPlatforms] =
    useState<TSocialMedia[]>(SOCIAL_MEDIA_LIST);

  const [selectedEcommerce, setSelectedEcommerce] =
    useState<TEcommerce[]>(ECOMMERCE_LIST);

  const { useGetEcommerceGroups } = useEcommerce();

  const handleClickOpen = () => {
    if (autoRefreshInterval > 0) {
      handleClickAlert({
        vertical: 'top',
        horizontal: 'center',
        severity: 'warning',
        message: 'You can manage objects only when auto refresh is off'
      });
      return;
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };
  const projectId = useAppSelector((state) => state.users.project.id);

  const { dashboardList, dashboardSelectedById } = useAppSelector(
    (state) => state.storeCustomDashboard
  );

  const objectList = useAppSelector(
    (state) => state.objectManagement.objectList.data
  );

  const filteredObjectsByType = useMemo(() => {
    if (objectList.length <= 0) return [];
    if (selectedObjectType === 'entity')
      return objectList.filter((object) => object.isEntity);
    if (selectedObjectType === 'object')
      return objectList.filter((object) => !object.isEntity);
    return objectList;
  }, [objectList, selectedObjectType]);

  const socialProfileObjList = useMemo(() => {
    return filteredObjectsByType.filter((obj) => {
      if (isGoogleReview) {
        return (
          obj.streamType === 'account' && obj.socialMedia === 'googleReview'
        );
      }
      if (isStreamPanel) {
        return obj.streamType === 'account';
      }
      return obj.streamType === 'account' && obj.socialMedia !== 'googleReview';
    });
  }, [filteredObjectsByType, isGoogleReview, isStreamPanel]);

  const filteredSocialProfileObjList = useMemo(() => {
    if (showFilter && !isGoogleReview)
      return isEcommerce
        ? ecommerceObjectList.filter(
            (object) => object.streamType === 'account'
          )
        : socialProfileObjList.filter((obj) =>
            selectedPlatforms.includes(obj.socialMedia)
          );
    if (isEcommerce)
      return ecommerceObjectList.filter(
        (object) => object.streamType === 'account'
      );
    return socialProfileObjList;
  }, [
    socialProfileObjList,
    selectedPlatforms,
    showFilter,
    isGoogleReview,
    selectedEcommerce,
    isEcommerce,
    ecommerceObjectList
  ]);

  const keywordObjList = useMemo(() => {
    if (showFilter && !isGoogleReview) {
      return isEcommerce
        ? ecommerceObjectList?.filter(
            (object) => object.streamType === 'keyword'
          )
        : filteredObjectsByType.filter(
            (obj) =>
              obj.streamType === 'keyword' &&
              selectedPlatforms.includes(obj.socialMedia)
          );
    }
    if (isEcommerce)
      return ecommerceObjectList?.filter(
        (object) => object.streamType === 'keyword'
      );
    return filteredObjectsByType.filter((obj) => obj.streamType === 'keyword');
  }, [
    filteredObjectsByType,
    selectedPlatforms,
    showFilter,
    isGoogleReview,
    isEcommerce,
    ecommerceObjectList,
    selectedEcommerce
  ]);

  const hashtagObjList = useMemo(() => {
    if (!isCampaign) return [];
    return keywordObjList.filter((obj) => obj.isHashtag);
  }, [keywordObjList, isCampaign]);

  const objectIdList = useAppSelector(
    (state) => state.analyticArguments.objectIdList
  );

  const {
    selectedGroups,
    groupSelectedById: selectedGroupId,
    groupObject: groupObjects
  } = useAppSelector((state) => state.objectManagement);

  const {
    data: ecommerceGroupData,
    // loading: ecommerceGroupLoading,
    // error: ecommerceGroupError,
    refetch: ecommerceGroupRefetch
  } = useGetEcommerceGroups({
    variables: {
      projectId,
      templateName: 'content'
      // objectGroupType: 'ecommerce'
    },
    skip: !isEcommerce
  });

  // get group name by selected group id
  const selectedGroup = useMemo(() => {
    if (isEcommerce) {
      if (!ecommerceGroupData) return null;
      const ecommerceGroups =
        ecommerceGroupData?.objectManagement_getObjectGroupList;
      return ecommerceGroups.find((group) => group.id === selectedGroupId);
    }

    // for socmed group
    const selectedGroup = groupObjects.data.find(
      (group) => group.id === selectedGroupId
    );
    return selectedGroup ?? null;
  }, [selectedGroupId, ecommerceGroupData, isEcommerce]);

  const [selectedGroupName, setSelectedGroupName] = useState<string>();

  useEffect(() => {
    setSelectedGroupName(selectedGroup?.displayName ?? '');
  }, [selectedGroup]);

  const activeObjectIds = useMemo(() => {
    const objectIdListSet = new Set(objectIdList);
    return isEcommerce
      ? ecommerceObjectList.filter((object) => objectIdListSet.has(object.id))
      : objectList.filter((object) => objectIdListSet.has(object.id));
  }, [objectIdList, isEcommerce, ecommerceObjectList]);

  const getActiveSocialProfileIds = () => {
    return activeObjectIds
      .filter((obj) => obj.streamType === 'account')
      .map((obj) => obj.id);
  };

  const getActiveKeywordAndHashtag = () => {
    return activeObjectIds
      .filter((object) => object.streamType === 'keyword')
      .map((object) => object.id);
  };

  const hashtagList = useAppSelector((state) =>
    state.storeSocialMediaCampaign.campaignSelected?.campaignObjectDetails
      ? state.storeSocialMediaCampaign.campaignSelected?.campaignObjectDetails.map(
          (campaign) => campaign.objectModel
        )
      : []
  );

  const [selectedSocialProfileIds, setSelectedSocialProfileIds] = useState<
    Array<string>
  >(getActiveSocialProfileIds);

  useEffect(() => {
    setSelectedSocialProfileIds(getActiveSocialProfileIds());
  }, [activeObjectIds, isEcommerce, ecommerceObjectList]);

  const [selectedKeywordIds, setSelectedKeywordIds] = useState<Array<string>>(
    getActiveKeywordAndHashtag
  );
  useEffect(() => {
    setSelectedKeywordIds(getActiveKeywordAndHashtag());
  }, [activeObjectIds, isEcommerce, ecommerceObjectList]);

  const [selectedHashtagIds, setSelectedHashtagIds] = useState<Array<string>>(
    hashtagList.map((object) => object.id)
  );

  // set active hashtag for campaign monitoring
  useEffect(() => {
    if (!isCampaign) return;
    const activeHashtagObjects = activeObjectIds.filter(
      (object) => object.isHashtag === true
    );
    const activeHashtagObjectIds = activeHashtagObjects.map(
      (object) => object.id
    );
    setSelectedHashtagIds(activeHashtagObjectIds);
  }, [isCampaign, activeObjectIds]);

  const isPageHaveAKeyword = () => {
    switch (pathSocialMedia) {
      case 'content':
        return false;
      case 'community':
        return false;
      default:
        return true;
    }
  };

  const handleClickDone = () => {
    const objectListActive = isCampaign
      ? selectedHashtagIds
      : isPageHaveAKeyword() || isEcommerce
      ? selectedSocialProfileIds.concat(selectedKeywordIds)
      : selectedSocialProfileIds;

    // edit group
    if (location.pathname === SETTINGS_GROUP) {
      if (isInactiveObjectExist(objectListActive)) {
        handleClickAlert({
          message: ADD_OBJECT_TO_GROUP_VALIDATION,
          severity: 'error',
          vertical: 'top',
          horizontal: 'center'
        });
        return;
      }

      const payload = {
        projectId: projectId,
        objectList: objectListActive,
        objectGroupId: selectedGroupId,
        groupName: selectedGroupName,
        templateName: 'content'
      };

      objectManagementEditGroup({ variables: payload }).then((response) => {
        if (isEcommerce) {
          ecommerceGroupRefetch({ objectGroupType: 'ecommerce' });
          onCloseEdit();
          return;
        }

        const editedGroup = response.data.objectManagement_editObjectGroup;
        const updatedGroups = groupObjects.data.map((group) => {
          if (editedGroup.id === group.id) return { ...editedGroup };
          return group;
        });

        const isEditedGroupActive = selectedGroups.some(
          (group) => group.id === editedGroup.id
        );
        if (isEditedGroupActive) {
          dispatch(reducerUpdateSelectedGroups([editedGroup]));
        }

        dispatch(
          reducerUpdateGroupObject({ ...groupObjects, data: updatedGroups })
        );
        dispatch(reducerUpdateObjectListByObjectType(editedGroup.objectList));
        dispatch(
          reducerUpdateAnalyticArgumentObjectIdList(
            editedGroup.objectList.map((object) => object.id)
          )
        );
      });
      onCloseEdit();
      return;
    }

    // edit custom dashboard
    if (isDashboard && isEditing) {
      const tempDashboard = dashboardSelectedById;
      const payload = {
        displayName: tempDashboard.displayName,
        objectIds: objectListActive,
        projectId,
        customDashboardId: tempDashboard.id
      };
      objectManagementEditDashboard({
        variables: payload
      }).then((response) => {
        const { id, objectList } =
          response.data?.customDashboard_editCustomDashboard;
        const objectIdList = objectList.map((object: IObject) => object.id);
        const updateDashboard = dashboardList.data.map((dashboard) => {
          if (dashboard.id === id) {
            return { ...dashboard, objectList };
          }
          return dashboard;
        });
        dispatch(
          reducerUpdateDashboardList({
            ...dashboardList,
            data: updateDashboard
          })
        );
        dispatch(reducerUpdateObjectListByObjectType(objectList));
        dispatch(reducerUpdateAnalyticArgumentObjectIdList(objectIdList));
        isEditing ? onCloseEdit() : handleClose();
      });
      return;
    }

    // for add new group via group selection drop down
    if (isEditing) {
      if (isInactiveObjectExist(objectListActive)) {
        handleClickAlert({
          message: ADD_OBJECT_TO_GROUP_VALIDATION,
          severity: 'error',
          vertical: 'top',
          horizontal: 'center'
        });
        return;
      }

      const payload = {
        projectId: projectId,
        objectList: objectListActive,
        objectGroupId: selectedGroupId,
        groupName: selectedGroupName,
        templateName: 'content'
      };

      objectManagementEditGroup({ variables: payload }).then((res) => {
        const editedGroup = res.data.objectManagement_editObjectGroup;
        const updatedGroups = groupObjects.data.map((group) => {
          if (editedGroup.id === group.id) return { ...editedGroup };
          return group;
        });

        dispatch(reducerUpdateSelectedGroups([editedGroup]));
        dispatch(
          reducerUpdateGroupObject({ ...groupObjects, data: updatedGroups })
        );
      });
    }

    // for change active object via modal
    const newObjectList = isEcommerce
      ? ecommerceObjectList
          .filter((val) => {
            return objectListActive.includes(val.id);
          })
          .map((val) => {
            return val;
          })
      : objectList
          .filter((val) => {
            return objectListActive.includes(val.id);
          })
          .map((val) => {
            return val;
          });

    dispatch(reducerUpdateObjectListByObjectType(newObjectList));
    dispatch(reducerUpdateAnalyticArgumentObjectIdList(objectListActive));
    isEditing ? onCloseEdit() : handleClose();
  };

  const handleChangeObjectType = (
    event: SelectChangeEvent<string>,
    child: React.ReactNode
  ) => {
    const selectedValue = event.target.value;
    dispatch(reducerUpdateSelectedObjectType(selectedValue as TObjectFilter));
    const isGroupActive = selectedGroupId !== '_default';
    const currentObjectList = isGroupActive
      ? selectedGroup.objectList
      : objectList;

    const objectListByType = currentObjectList.filter((object) => {
      if (selectedValue === 'entity') return object.isEntity;
      if (selectedValue === 'object') return !object.isEntity;
      return true;
    });

    // set active objects based on object type
    setSelectedSocialProfileIds(
      objectListByType
        .filter((object) => object.streamType === 'account')
        .map((object) => object.id)
    );
    setSelectedKeywordIds(
      objectListByType
        .filter((object) => object.streamType === 'keyword')
        .map((object) => object.id)
    );
    if (isCampaign) {
      setSelectedHashtagIds(
        objectListByType
          .filter(
            (object) => object.streamType === 'keyword' && object.isHashtag
          )
          .map((obj) => obj.id)
      );
    }
  };

  const handlePlatformChange = (platform: TSocialMedia) => {
    if (selectedPlatforms.length <= 1) return;
    setSelectedPlatforms((prev) =>
      prev.includes(platform)
        ? prev.filter((p) => p !== platform)
        : [...prev, platform]
    );
  };

  const handleClickAll = () => {
    setSelectedPlatforms(SOCIAL_MEDIA_LIST);
  };

  const handlePlatformChangeEcommerce = (platform: TEcommerce) => {
    if (selectedEcommerce.length <= 1) return;
    setSelectedEcommerce((prev) =>
      prev.includes(platform)
        ? prev.filter((p) => p !== platform)
        : [...prev, platform]
    );
  };

  const handleClickAllEcommerce = () => {
    setSelectedEcommerce(ECOMMERCE_LIST);
  };

  const {
    isOnTour,
    currentTour: { stepNumber, submenu, menu }
  } = useAppSelector((state) => state.storeProductTour);

  const isCurrentlyOnTourGroupStep6 =
    stepNumber === 6 && isOnTour && submenu === 'group';

  const isCurrentlyOnTourSocmedOverviewStep11 =
    isOnTour &&
    stepNumber === 11 &&
    menu === 'social-media' &&
    submenu === 'overview';

  return (
    <>
      {isHidden ? null : (
        <Stack
          onClick={!isOnTour ? handleClickOpen : undefined}
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            backgroundColor: '#0B85AE',
            px: '11px',
            py: '8px',
            borderRadius: '8px',
            cursor: 'pointer',
            ':hover': {
              opacity: 0.5
            }
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6.66667 3.33398H2"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M8 12.666H2"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M9.33398 2V4.66667"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M10.666 11.334V14.0007"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M14 8H8"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M13.9993 12.666H10.666"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M14.0007 3.33398H9.33398"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M5.33398 6.66602V9.33268"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M5.33333 8H2"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <Typography fontWeight={400} color="white">
            Manage {title ?? 'Objects'}
          </Typography>
        </Stack>
      )}

      <Dialog
        onClose={
          isOnTour
            ? undefined
            : isEditing
            ? () => {
                setSelectedGroupName(selectedGroup?.displayName ?? '');
                onCloseEdit();
              }
            : handleClose
        }
        open={
          isCurrentlyOnTourSocmedOverviewStep11
            ? isCurrentlyOnTourSocmedOverviewStep11
            : isEditing
            ? isEditing
            : open
        }
        fullWidth
        maxWidth={'md'}
        sx={{
          opacity: hide ? 0 : 1,
          ...(isOnTour ? { pointerEvents: 'none' } : {})
        }}
      >
        <IconButton
          aria-label="close"
          onClick={
            isOnTour
              ? undefined
              : isEditing
              ? () => {
                  setSelectedGroupName(selectedGroup?.displayName ?? '');
                  onCloseEdit();
                }
              : handleClose
          }
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
        <ProductTourTooltip
          placement="right"
          open={
            isCurrentlyOnTourGroupStep6 || isCurrentlyOnTourSocmedOverviewStep11
          }
          maxWidth={180}
          onClick={onClickNextTour}
          onClose={() => {
            if (onClickNextTour) {
              onClickNextTour();
            }
            handleClose();
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ pt: 8, px: 6 }}
          >
            <Typography variant="h3">Manage {title ?? 'Objects'}</Typography>
            {!isEditing ? (
              <Select
                id="objectType"
                name="objectType"
                value={selectedObjectType}
                onChange={handleChangeObjectType}
                sx={{ minWidth: '140px' }}
              >
                <MenuItem key="placeholder" id="placeholder" value="" disabled>
                  Object Type
                </MenuItem>
                {objectTypeOptions.map(({ id, value, label }) => (
                  <MenuItem
                    key={id}
                    id={id}
                    value={value}
                    // disabled={
                    //   // disable filtering when group is active
                    //   isGroupActive && (id === 'entity' || id === 'object')
                    // }
                  >
                    {label}
                  </MenuItem>
                ))}
              </Select>
            ) : null}
          </Stack>
        </ProductTourTooltip>
        {isCampaign ? (
          <SectionHashtag
            objectList={hashtagObjList}
            objectListIdActive={selectedHashtagIds}
            onParentVisible={() => setHide((prev) => !prev)}
            onSelect={(hashtagIds) => setSelectedHashtagIds(hashtagIds)}
          />
        ) : (
          <>
            {isEditing && (
              <Stack sx={{ mt: theme.spacing(4), mx: theme.spacing(5) }}>
                <DialogContentText sx={{ mb: theme.spacing(0.5) }}>
                  Group name
                </DialogContentText>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={selectedGroupName}
                  onChange={(e) => setSelectedGroupName(e.target.value)}
                />
              </Stack>
            )}
            {showFilter && !isGoogleReview && (
              <Stack
                direction="row"
                sx={{ mt: theme.spacing(4), mx: theme.spacing(5) }}
              >
                {isEcommerce ? (
                  <FilterObjectByPlatformEcommerce
                    selectedPlatforms={selectedEcommerce}
                    handleChange={
                      !isOnTour ? handlePlatformChangeEcommerce : () => {}
                    }
                    handleClickAll={
                      !isOnTour ? handleClickAllEcommerce : () => {}
                    }
                  />
                ) : (
                  <FilterObjectByPlatform
                    selectedPlatforms={selectedPlatforms}
                    handleChange={!isOnTour ? handlePlatformChange : () => {}}
                    handleClickAll={!isOnTour ? handleClickAll : () => {}}
                  />
                )}
              </Stack>
            )}
            <SectionSocialProfile
              objectList={filteredSocialProfileObjList}
              objectListIdActive={selectedSocialProfileIds}
              onParentVisible={
                !isOnTour ? () => setHide((prev) => !prev) : undefined
              }
              onSelect={
                !isOnTour
                  ? (socialProfileIds) =>
                      setSelectedSocialProfileIds(socialProfileIds)
                  : () => {}
              }
            />
            {isPageHaveAKeyword() && !isGoogleReview && (
              <SectionKeywordHashtag
                objectList={keywordObjList}
                objectListIdActive={selectedKeywordIds}
                onParentVisible={
                  !isOnTour ? () => setHide((prev) => !prev) : undefined
                }
                onSelect={
                  !isOnTour
                    ? (socialProfileIds) =>
                        setSelectedKeywordIds(socialProfileIds)
                    : () => {}
                }
              />
            )}
          </>
        )}

        <DialogActions
          sx={{
            display: 'flex',
            justifyContent: 'center',
            pb: theme.spacing(5)
          }}
        >
          <Button
            variant="contained"
            sx={{
              background: theme.palette.primary.dark,
              width: theme.spacing(20),
              color: theme.colors.primary.lighter
            }}
            size="large"
            onClick={!isOnTour ? handleClickDone : undefined}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ModalManageObjectByGroup;
